/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { Button, KIND } from "baseui/button";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

export interface TableNameButtonProps {
  /** The text shown (the folder or file name). */
  label: string;
  /** Invoked on click — open the folder, or open the file's contents modal. */
  onClick: () => void;
  /**
   * Folder names read as links (different color) to signal navigating into them; file
   * names stay neutral table text. Defaults to "file".
   */
  variant?: "folder" | "file";
}

/**
 * The clickable name cell in the Files table, rendered as a baseui Button so it
 * will be governed by the BaseProvider theme.
 *
 * NOTE: The overrides below are TRANSITIONAL: they only exist to make the Button read
 * as an inline link today. Once the BaseProvider theme governs Button, thin or
 * delete them here (one place) and every name cell inherits the central styling.
 */
export function TableNameButton({
  label,
  onClick,
  variant = "file",
}: TableNameButtonProps): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const { colors, fonts, fontSizes } = currentTheme;
  const color = variant === "folder" ? colors.linkColor : colors.tableTextColor;

  return (
    <Button
      kind={KIND.tertiary}
      onClick={onClick}
      overrides={{
        BaseButton: {
          // transitional: strip the Button chrome so it reads as an inline link.
          style: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            backgroundColor: "transparent",
            color,
            fontFamily: fonts.primary,
            fontSize: fontSizes.medium,
            fontWeight: "normal",
            display: "inline-flex",
            alignItems: "center",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ":hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            ":focus": { backgroundColor: "transparent" },
            ":active": { backgroundColor: "transparent" },
          },
        },
      }}
    >
      {label}
    </Button>
  );
}

export default TableNameButton;
